import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage, useLp } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { SEO } from '../components/SEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const privacyConsentText = {
  it: 'Ho letto e accetto la',
  en: 'I have read and accept the',
  de: 'Ich habe die gelesen und akzeptiere die',
  fr: 'J\'ai lu et j\'accepte la'
};

const ContactPage = () => {
  const { t, language } = useLanguage();
  const lp = useLp();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const offices = [
    {
      key: 'valeggio',
      mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2797.8!2d10.7333!3d45.3533!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDIxJzEyLjAiTiAxMMKwNDQnMDAuMCJF!5e0!3m2!1sen!2sit!4v1234567890'
    },
    {
      key: 'peschiera',
      mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2797.8!2d10.6833!3d45.4386!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDI2JzE5LjAiTiAxMMKwNDEnMDAuMCJF!5e0!3m2!1sen!2sit!4v1234567890'
    },
    {
      key: 'verona',
      mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2797.8!2d10.9917!3d45.4384!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDI2JzE4LjAiTiAxMMKwNTknMzAuMCJF!5e0!3m2!1sen!2sit!4v1234567890'
    }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      setStatus({ type: 'error', message: t('ui.contact.privacyError') });
      return;
    }
    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      await axios.post(`${API}/contact`, { ...formData, privacy_accepted: true, language });
      setStatus({ type: 'success', message: t('contact.form.success') });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setPrivacyAccepted(false);
    } catch (error) {
      setStatus({ type: 'error', message: t('contact.form.error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen">
      <SEO page="contact" />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#121826]" />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A227]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-4">
              Accordini Carillo
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 max-w-3xl">
              {t('contact.title')}
            </h1>
            <p className="text-white/45 text-base md:text-lg max-w-2xl leading-relaxed">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 md:py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#283880] mb-8">
                {t('contact.form.send')}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#64748B] mb-2">
                      {t('contact.form.name')} *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name"
                      className="h-12 border-slate-200 focus:border-[#C9A227] focus:ring-[#4A5FC1]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#64748B] mb-2">
                      {t('contact.form.email')} *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email"
                      className="h-12 border-slate-200 focus:border-[#C9A227] focus:ring-[#4A5FC1]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#64748B] mb-2">
                      {t('contact.form.phone')}
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-phone"
                      className="h-12 border-slate-200 focus:border-[#C9A227] focus:ring-[#4A5FC1]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#64748B] mb-2">
                      {t('contact.form.subject')} *
                    </label>
                    <Input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      data-testid="contact-subject"
                      className="h-12 border-slate-200 focus:border-[#C9A227] focus:ring-[#4A5FC1]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#64748B] mb-2">
                    {t('contact.form.message')} *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    data-testid="contact-message"
                    className="border-slate-200 focus:border-[#C9A227] focus:ring-[#4A5FC1]"
                  />
                </div>

                {/* Privacy Consent Checkbox - GDPR Required */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy-consent"
                    checked={privacyAccepted}
                    onCheckedChange={setPrivacyAccepted}
                    data-testid="privacy-consent-checkbox"
                    className="mt-1 border-slate-300 data-[state=checked]:bg-[#C9A227] data-[state=checked]:border-[#C9A227]"
                  />
                  <label htmlFor="privacy-consent" className="text-sm text-[#64748B] leading-relaxed cursor-pointer">
                    {privacyConsentText[language] || privacyConsentText.it}{' '}
                    <Link 
                      to={lp('/privacy-policy')} 
                      className="text-[#C9A227] hover:underline font-medium"
                      target="_blank"
                    >
                      Privacy Policy
                    </Link>
                    {' *'}
                  </label>
                </div>

                {status.type && (
                  <div 
                    data-testid="contact-status"
                    className={`flex items-center gap-2 p-4 ${
                      status.type === 'success' 
                        ? 'bg-green-50 text-green-700' 
                        : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {status.type === 'success' ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <AlertCircle className="w-5 h-5" />
                    )}
                    {status.message}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                  className="bg-[#C9A227] text-[#283880] hover:bg-[#B8911F] h-12 px-8 uppercase tracking-widest text-xs font-bold disabled:opacity-50"
                >
                  {isSubmitting ? '...' : t('contact.form.send')}
                  <Send className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#283880] mb-8">
                {t('footer.contacts')}
              </h2>
              
              {offices.map((office) => (
                <div 
                  key={office.key}
                  data-testid={`office-${office.key}`}
                  className="p-6 bg-[#F8F9FA] border border-slate-100"
                >
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#283880] mb-4">
                    {t(`contact.offices.${office.key}.name`)}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[#64748B] text-sm">
                          {t(`contact.offices.${office.key}.address`)}
                        </p>
                        <p className="text-[#64748B] text-sm">
                          {t(`contact.offices.${office.key}.city`)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-[#C9A227] flex-shrink-0" />
                      <a 
                        href={`tel:${t(`contact.offices.${office.key}.phone`).replace(/\s/g, '')}`}
                        className="text-[#283880] text-sm font-medium hover:text-[#C9A227] transition-colors"
                      >
                        {t(`contact.offices.${office.key}.phone`)}
                      </a>
                    </div>
                  </div>
                </div>
              ))}

              {/* General Email */}
              <div className="p-6 bg-[#283880] text-white">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#C9A227]" />
                  <a 
                    href="mailto:info@accordinicarillo.it"
                    className="text-white hover:text-[#C9A227] transition-colors"
                  >
                    info@accordinicarillo.it
                  </a>
                </div>
                <p className="mt-4 text-slate-400 text-sm">
                  P.IVA: IT05171120230
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#283880]">
              {t('contact.subtitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {offices.map((office) => (
              <motion.div
                key={office.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="overflow-hidden"
              >
                <div className="h-64 bg-slate-200 grayscale hover:grayscale-0 transition-all duration-500">
                  <iframe
                    src={office.mapUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={t(`contact.offices.${office.key}.name`)}
                  />
                </div>
                <div className="p-4 bg-white text-center">
                  <p className="font-medium text-[#283880] text-sm">
                    {t(`contact.offices.${office.key}.name`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
